import { Injectable } from '@angular/core';

import { EMPTY, expand, finalize, map, Observable, of, reduce, tap } from 'rxjs';

import { IdentityOrganization, IPagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save identity organizations.
 */
@Injectable()
export class IdentityOrganizationService {
    public static PARAM_USER_PERMISSIONS_META: string = 'userPermissionsMeta';

    private identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityOrganization[]; } = {};
    private cachedOrg: { [apiRequest: string]: IdentityOrganization; } = {};

    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    public clearCache(): void {
        this.cachedMap = {};
        this.cachedOrg = {};
    }

    /**
     * Recursively get all organizations
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityOrganization[]> {
        queryParams = queryParams || [];

        if (queryParams.filter((x: string) => x.indexOf('page[size]') !== -1).length === 0) {
            queryParams.push('page[size]=500');
        }

        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh && this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityOrganization) => a.clone()),
            );
        }
        // Make the http request
        // Recursively get all organizations
        return this.http.get(apiRequestUrl)
            .pipe(
                expand((response: IPagedResponse) => {
                    const json: IPagedResponse = response;

                    if (json.links && json.links.next) {
                        // If you have more values we need to call http again. This is done recursively
                        return this.http.get(json.links.next, false, false);
                    }
                    return EMPTY;
                }),
                // Deserialize the response data into an array of IdentityOrganizations
                map((response: IJsonApiResponse) => {
                    const bodyData: any = response.data || [];
                    return bodyData.map((json: any) => new IdentityOrganization().deserialize(json));
                }),
                // Concatenate the new array of IdentityOrganizations with the previous array
                reduce((previousValue: IdentityOrganization[], currentValue: IdentityOrganization[]) =>
                    previousValue.concat(currentValue)),
                // Finally, update cached map and return the aggregated organizations
                tap((returnData: IdentityOrganization[]) => {
                    this.cachedMap[apiRequestUrl] = returnData.map((x: IdentityOrganization) => x.clone());
                    return this.cachedMap[apiRequestUrl];
                }),
            );
    }

    /**
     * Get a list of organizations the user has modify access to.
     */
    public getUserOrganizations(includes: string[] = null): Observable<IdentityOrganization[]> {
        const queryParams: string[] = ['userGuidWithModifyAccess=current'];
        return this.getIndex(includes, queryParams, true);
    }

    /**
     * Get a single organization
     */
    public get(
        identityOrganizationId: string,
        includes: string[] = null,
        queryParams: string[] = [],
    ): Observable<IdentityOrganization> {
        if (queryParams.filter(
            (x: string) => x.indexOf(IdentityOrganizationService.PARAM_USER_PERMISSIONS_META) !== -1,
        ).length === 0) {
            queryParams.push(`${IdentityOrganizationService.PARAM_USER_PERMISSIONS_META}=1`);
        }

        const apiRequestUrl: string = this.getApiUrl(identityOrganizationId, includes, queryParams);

        if (this.cachedOrg[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedOrg[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const organization: IdentityOrganization = (new IdentityOrganization().deserialize(response));
                this.cachedOrg[apiRequestUrl] = organization.clone();
                return organization;
            }));
    }

    /**
     * Add or update a organization
     */
    public save(
        organization: IdentityOrganization,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityOrganization> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(organization.id, includes, queryParams);

        if (organization.id) {
            // Update existing Organization
            response = this.http.patch(apiRequestUrl, organization.serialize());
        } else {
            // Add a new Organization
            response = this.http.post(apiRequestUrl, organization.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityOrganization()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Request access to multiple teams in the given organization
     */
    public requestTeamAccess(organizationId: string, teamIds: string[], allTeams: boolean): Observable<number> {
        const apiRequestUrl: string = this.getApiUrl(`${organizationId}/team-claims`);

        const body: any = {
            data: {
                id: organizationId,
                attributes: {
                    teamIds: teamIds,
                    allTeams: allTeams,
                },
            },
        };

        const response: Observable<IJsonApiResponse> = this.http.post(apiRequestUrl, JSON.stringify(body));

        return response
            .pipe(
                map((response: IJsonApiResponse) => {
                    const responseBody: any = response;
                    return responseBody.claimsCreated;
                }),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified organization.
     */
    public delete(identityOrganizationId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityOrganizationId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Add one or more teams to the given identity organization
     *
     * @param {string} identityOrganizationId The identity organization id
     * @param {string[]} identityTeamIds The list of teams to link to the given organization
     * @returns {Observable<IdentityOrganization>}
     */
    public addTeams(
        identityOrganizationId: string,
        identityTeamIds: string[],
    ): Observable<IdentityOrganization> {
        let apiRequestUrl: string = this.getApiUrl(identityOrganizationId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = {};
        json.data = [];

        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.post(apiRequestUrl, JSON.stringify(json), true, false)
            .pipe(map((response: IJsonApiResponse) => (new IdentityOrganization().deserialize(response))));
    }

    /**
     * Delete one or more teams linked the given identity organization
     *
     * @param {string} identityOrganizationId The identity organization id
     * @param {string[]} identityTeamIds The list of teams to link to the given organization
     * @returns {Observable<IdentityOrganization>}
     */
    public deleteTeams(identityOrganizationId: string, identityTeamIds: string[]): Observable<IdentityOrganization> {
        let apiRequestUrl: string = this.getApiUrl(identityOrganizationId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = {};
        json.data = [];
        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.delete(apiRequestUrl, true, false, this.http.getHeadersForJson(), JSON.stringify(json))
            .pipe(map((response: IJsonApiResponse) => (new IdentityOrganization()).deserialize(response)));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        organizationId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/organizations/`;

        if (organizationId) {
            url += organizationId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

